import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useStartEnvironment, useStopEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { DeleteEnvironmentModal, ForceDeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { useCanStartEnvironment } from "@/hooks/use-can-start-environment";
import { canOpen, showStart, showStop } from "@/routes/environments/phase";
import { useEnvironmentSupportBundle } from "@/hooks/use-environment-support-bundle";
import { useLifetimeEnforcement } from "@/hooks/use-lifetime-enforcement";
import { formatError } from "@/utils/errors";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type React from "react";
import { useCallback, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import type { PlainEditor } from "@/queries/editor-queries";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import {
    ContextMenuItem,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuPortal,
    ContextMenuContent,
    ContextMenuSeparator,
} from "@/components/flexkit/ContextMenu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";
import { ResourceUsageDialog } from "@/components/environments/ResourceUsageDialog";
import { Tooltip } from "@/components/Tooltip";

import { IconCode } from "@/assets/icons/geist/IconCode";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconOnaCopy } from "@/assets/icons/IconOnaCopy";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";
import { IconOnaFolder } from "@/assets/icons/IconOnaFolder";
import { withGitpodSize } from "@/assets/icons/utils";
import { IconFileText } from "central-icons/IconFileText";
import { IconHeartBeat } from "central-icons/IconHeartBeat";

const IconFileTextSized = withGitpodSize(IconFileText);
import { IconConsole } from "central-icons/IconConsole";

const IconHeartBeatSized = withGitpodSize(IconHeartBeat);
const IconConsoleSized = withGitpodSize(IconConsole);

export const EnvironmentActionsMenu: FC<{
    environment: PlainEnvironment;
    handleViewDetails?: () => void;
    handleGoToProject?: () => void;
    defaultEditor: PlainEditor | undefined;
    startRename: (() => void) | undefined;
    children: React.ReactNode;
    renderAs: "dropdownMenu" | "contextMenu";
    onOpenChange?: (open: boolean) => void;
    agentExecution?: PlainAgentExecution;
    open?: boolean;
}> = ({
    environment,
    handleViewDetails,
    handleGoToProject,
    renderAs,
    children,
    defaultEditor,
    startRename,
    onOpenChange,
    agentExecution,
    open,
}) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const canStartEnvironment = useCanStartEnvironment(environment);
    const { data: runner } = useRunner(environment.metadata?.runnerId);

    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch, isTokenReady } =
        useEnvironmentSupportBundle(environment);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const [showResourceUsage, setShowResourceUsage] = useState(false);
    const pendingRenameRef = useRef(false);
    const { handleStartError, modalProps } = useLifetimeEnforcement();

    const allowForceDelete = runner && runner.provider !== RunnerProvider.MANAGED;

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                enableTokenFetch();
            }
            onOpenChange?.(isOpen);
            // Start rename when the menu closes. The ref stays set so
            // handleCloseAutoFocus (which fires later via setTimeout(0))
            // can still prevent Radix's FocusScope from stealing focus.
            if (!isOpen && pendingRenameRef.current) {
                startRename?.();
            }
        },
        [enableTokenFetch, onOpenChange, startRename],
    );

    const handleViewLogs = useCallback(() => {
        navigate(`/details/${environment.id}/logs`);
    }, [environment.id, navigate]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({
                environmentId: environment.id,
                skipPolicyCheck: true,
            });
        } catch (error) {
            if (handleStartError(error)) {
                return;
            }
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, handleStartError, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    const openEditor = useOpenEditor();
    const handleStop = useCallback(async () => {
        try {
            await stopEnvironment.mutateAsync(environment.id);
        } catch (error) {
            toast({
                title: "Failed to stop environment",
                description: formatError(error),
            });
        }
    }, [stopEnvironment, toast, environment.id]);

    const handleDelete = useCallback(() => setShowDeleteModal(true), [setShowDeleteModal]);
    const handleForceDelete = useCallback(() => setShowForceDeleteModal(true), [setShowForceDeleteModal]);
    const handleRenameSelect = useCallback(() => {
        pendingRenameRef.current = true;
    }, []);
    const handleCloseAutoFocus = useCallback((event: Event) => {
        if (!pendingRenameRef.current) {
            return;
        }
        // Renaming swaps the menu trigger out for an inline form. If Radix
        // restores focus to the old trigger, the editor blurs and closes.
        event.preventDefault();
        pendingRenameRef.current = false;
    }, []);

    const isPointerCoarse = useIsPointerCoarse();

    const Root = renderAs === "dropdownMenu" ? DropdownMenu : ContextMenuRoot;
    const Trigger = renderAs === "dropdownMenu" ? DropdownMenuTrigger : ContextMenuTrigger;
    const MenuItem = renderAs === "dropdownMenu" ? DropdownMenuItem : ContextMenuItem;
    const Content = renderAs === "dropdownMenu" ? DropdownMenuContent : ContextMenuContent;
    const Portal = renderAs === "dropdownMenu" ? "div" : ContextMenuPortal; // dropdownMenu already has a portal
    const Separator = renderAs === "dropdownMenu" ? DropdownMenuSeparator : ContextMenuSeparator;

    // On touch devices, disable the Radix context menu trigger to prevent its
    // built-in 700ms long-press from firing during scroll gestures and locking
    // the page. The dropdown menu (via react-aria useLongPress) handles mobile
    // long-press instead.
    const disableContextTrigger = renderAs === "contextMenu" && isPointerCoarse;

    return (
        <>
            <Root modal={true} onOpenChange={handleOpenChange} open={open}>
                {renderAs === "dropdownMenu" ? (
                    <Trigger asChild>{children}</Trigger>
                ) : (
                    <Trigger disabled={disableContextTrigger}>{children}</Trigger>
                )}
                <Portal>
                    {/* DropdownMenuItem has mx-1 built-in, ContextMenuItem doesn't - use px-0 for dropdown to match */}
                    <Content
                        className={cn("min-w-[220px] rounded-md bg-surface-01 p-1 shadow-lg", {
                            "px-0 py-1": renderAs === "dropdownMenu",
                        })}
                        onCloseAutoFocus={handleCloseAutoFocus}
                    >
                        {/* Open in editor */}
                        <MenuItem
                            onClick={() => defaultEditor && openEditor(defaultEditor, environment)}
                            disabled={!canOpen(environment)}
                            LeadingIcon={IconCode}
                            data-tracking-id="open-environment-environment-actions-menu"
                        >
                            Open in editor
                        </MenuItem>

                        <Separator />

                        {/* Section 1: Links / navigation */}
                        {handleViewDetails && (
                            <MenuItem
                                onClick={handleViewDetails}
                                LeadingIcon={IconFileTextSized}
                                data-tracking-id="view-environment-details-environment-actions-menu"
                            >
                                Details
                            </MenuItem>
                        )}
                        <MenuItem
                            onClick={handleViewLogs}
                            LeadingIcon={IconConsoleSized}
                            data-testid="environment-actions-dropdown-view-logs"
                            data-tracking-id="view-environment-logs-environment-actions-menu"
                        >
                            Logs
                        </MenuItem>
                        <MenuItem
                            onClick={() => setShowResourceUsage(true)}
                            LeadingIcon={IconHeartBeatSized}
                            data-testid="environment-actions-menu-resource-usage"
                            data-tracking-id="resource-usage-environment-actions-menu"
                        >
                            Resource usage
                        </MenuItem>
                        <Tooltip
                            content={!handleGoToProject ? "No project associated with this environment" : undefined}
                        >
                            <div>
                                <MenuItem
                                    onClick={handleGoToProject}
                                    disabled={!handleGoToProject}
                                    LeadingIcon={IconOnaFolder}
                                    data-tracking-id="go-to-project-environment-actions-menu"
                                >
                                    Go to project
                                </MenuItem>
                            </div>
                        </Tooltip>

                        <Separator />

                        {/* Section 2: Debug actions */}
                        <MenuItem
                            onClick={handleCopyId}
                            LeadingIcon={IconOnaCopy}
                            data-tracking-id="copy-environment-id-environment-actions-menu"
                        >
                            Copy ID
                        </MenuItem>
                        {isSupportBundleAvailable && (
                            <MenuItem
                                onClick={downloadSupportBundle}
                                disabled={!isTokenReady}
                                LeadingIcon={IconOnaDownload}
                                data-testid="environment-actions-menu-download-support-bundle"
                                data-tracking-id="download-support-bundle-environment-actions-menu"
                            >
                                Download support bundle
                            </MenuItem>
                        )}

                        <Separator />

                        {/* Section 3: Environment actions */}
                        {showStart(environment) && (
                            <MenuItem
                                onClick={handleStart}
                                disabled={!canStartEnvironment}
                                LeadingIcon={(props) => (
                                    <IconPlay {...props} className={cn(props.className, "text-content-secondary")} />
                                )}
                                data-testid="environment-actions-dropdown-start"
                                data-tracking-id="start-environment-environment-actions-menu"
                            >
                                Start
                            </MenuItem>
                        )}
                        {showStop(environment) && (
                            <MenuItem
                                onClick={handleStop}
                                LeadingIcon={(props) => (
                                    <IconStop {...props} className={cn(props.className, "text-content-secondary")} />
                                )}
                                data-testid="environment-actions-dropdown-stop"
                                data-tracking-id="stop-environment-environment-actions-menu"
                            >
                                Stop
                            </MenuItem>
                        )}
                        <MenuItem
                            onSelect={handleRenameSelect}
                            LeadingIcon={IconEdit}
                            data-tracking-id="rename-environment-environment-actions-menu"
                        >
                            Rename
                        </MenuItem>
                        {/* Section 4: Destructive */}
                        {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                            <>
                                <Separator />
                                <MenuItem
                                    variant="destructive"
                                    onClick={handleDelete}
                                    LeadingIcon={IconTrash}
                                    data-tracking-id="delete-environment-environment-actions-menu"
                                >
                                    Delete
                                </MenuItem>
                            </>
                        )}
                        {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && allowForceDelete && (
                            <>
                                <Separator />
                                <MenuItem
                                    onClick={handleForceDelete}
                                    variant="destructive"
                                    LeadingIcon={IconTrash}
                                    data-tracking-id="force-delete-environment-environment-actions-menu"
                                >
                                    Force delete
                                </MenuItem>
                            </>
                        )}
                    </Content>
                </Portal>
            </Root>
            <DeleteEnvironmentModal
                environment={environment}
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                agentExecution={agentExecution}
            />
            <ForceDeleteEnvironmentModal
                environment={environment}
                open={showForceDeleteModal}
                onClose={() => setShowForceDeleteModal(false)}
                agentExecution={agentExecution}
            />
            <EnvironmentLifetimeExceededModal environment={environment} {...modalProps} />
            <ResourceUsageDialog
                environmentId={environment.id}
                open={showResourceUsage}
                onOpenChange={setShowResourceUsage}
            />
        </>
    );
};
